import Splide from '@splidejs/splide';
import { isPageSliderActive } from '../page-slider.js';

const personagensSplides = new Map();
const activeSectionChangeEventName = 'section-keyboard:active-section-change';
const mobileSectionObserverThreshold = 0.5;

const personagensFallbackInterval = 2500;
const personagensFallbackSpeed = 980;
const personagensFallbackEasing = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
const featuredSlideClassName = 'personagens__slide--featured';
const featuredNameSlideClassName = 'personagens__name-slide--featured';
const featuredSpotlightClassName = 'personagens__slide--spotlight';

const personagensBaseOptions = {
  type: 'loop',
  start: 2,
  perPage: 5,
  perMove: 1,
  focus: 'center',
  gap: 0,
  updateOnMove: true,
  pagination: false,
  arrows: false,
  breakpoints: {
    899: {
      perPage: 3,
    },
    599: {
      perPage: 1,
    },
  },
};

function getPersonagensCssNumber(section, propertyName, fallbackValue) {
  const propertyValue = getComputedStyle(section).getPropertyValue(propertyName).trim();
  const parsedValue = Number.parseFloat(propertyValue);

  if (!Number.isFinite(parsedValue)) {
    return fallbackValue;
  }

  return parsedValue;
}

function getPersonagensCssString(section, propertyName, fallbackValue) {
  const propertyValue = getComputedStyle(section).getPropertyValue(propertyName).trim();

  if (!propertyValue) {
    return fallbackValue;
  }

  return propertyValue;
}

function getPersonagensBaseOptions(section) {
  return {
    ...personagensBaseOptions,
    interval: getPersonagensCssNumber(section, '--personagens-carousel-interval-ms', personagensFallbackInterval),
    speed: getPersonagensCssNumber(section, '--personagens-carousel-speed-ms', personagensFallbackSpeed),
    easing: getPersonagensCssString(section, '--personagens-carousel-easing', personagensFallbackEasing),
  };
}

function shouldAllowAutoplay() {
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getInitialDesktopActiveSection() {
  if (!isPageSliderActive()) {
    return null;
  }

  return document.querySelector('#page-slider .splide__slide.is-active section');
}

function getViewportVisibilityRatio(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, viewportHeight);
  const visibleHeight = Math.max(visibleBottom - visibleTop, 0);

  if (rect.height <= 0) {
    return 0;
  }

  return visibleHeight / rect.height;
}

function isPersonagensVisibleSlide(slide) {
  if (typeof slide.isVisible === 'function') {
    return slide.isVisible();
  }

  return Boolean(slide.slide && slide.slide.classList.contains('is-visible'));
}

function clearFeaturedPersonagem(instance, featuredClassName) {
  instance.Components.Slides.get().forEach((slide) => {
    if (!slide.slide) {
      return;
    }

    slide.slide.classList.remove(featuredClassName);
  });
}

function clearFeaturedSpotlight(instance) {
  instance.Components.Slides.get().forEach((slide) => {
    if (!slide.slide) {
      return;
    }

    slide.slide.classList.remove(featuredSpotlightClassName);
  });
}

function getSlideTrackIntersection(slideRect, trackRect) {
  const intersectionLeft = Math.max(slideRect.left, trackRect.left);
  const intersectionRight = Math.min(slideRect.right, trackRect.right);

  return Math.max(0, intersectionRight - intersectionLeft);
}

function getPersonagensCenteredSlide(instance) {
  const slides = instance.Components.Slides.get();
  const trackRect = instance.root.querySelector('.splide__track').getBoundingClientRect();
  const trackCenter = trackRect.left + (trackRect.width / 2);
  let closestSlide = null;
  let closestDistance = Number.POSITIVE_INFINITY;
  let strongestIntersection = 0;

  slides.forEach((slide) => {
    if (!slide.slide) {
      return;
    }

    const slideRect = slide.slide.getBoundingClientRect();
    const trackIntersection = getSlideTrackIntersection(slideRect, trackRect);

    if (trackIntersection <= 0) {
      return;
    }

    const slideCenter = slideRect.left + (slideRect.width / 2);
    const centerDistance = Math.abs(slideCenter - trackCenter);

    if (trackIntersection > strongestIntersection || (trackIntersection === strongestIntersection && centerDistance < closestDistance)) {
      closestSlide = slide;
      strongestIntersection = trackIntersection;
      closestDistance = centerDistance;
    }
  });

  if (closestSlide) {
    return closestSlide;
  }

  slides.forEach((slide) => {
    if (!slide.slide || !isPersonagensVisibleSlide(slide)) {
      return;
    }

    const slideRect = slide.slide.getBoundingClientRect();
    const slideCenter = slideRect.left + (slideRect.width / 2);
    const centerDistance = Math.abs(slideCenter - trackCenter);

    if (centerDistance < closestDistance) {
      closestSlide = slide;
      closestDistance = centerDistance;
    }
  });

  return closestSlide;
}

function applyFeaturedPersonagem(carousel, names) {
  const featuredCarouselSlide = getPersonagensCenteredSlide(carousel);
  const featuredNameSlide = getPersonagensCenteredSlide(names);

  if (featuredCarouselSlide && featuredCarouselSlide.slide) {
    featuredCarouselSlide.slide.classList.add(featuredSlideClassName);
  }

  if (featuredNameSlide && featuredNameSlide.slide) {
    featuredNameSlide.slide.classList.add(featuredNameSlideClassName);
  }
}

function getBestPersonagensSlideMatch(instance, slideMatcher) {
  const slides = instance.Components.Slides.get();
  const trackRect = instance.root.querySelector('.splide__track').getBoundingClientRect();
  const trackCenter = trackRect.left + (trackRect.width / 2);
  let matchedSlide = null;
  let strongestIntersection = 0;
  let closestDistance = Number.POSITIVE_INFINITY;

  slides.forEach((slide) => {
    if (!slide.slide || !slideMatcher(slide.slide)) {
      return;
    }

    const slideRect = slide.slide.getBoundingClientRect();
    const trackIntersection = getSlideTrackIntersection(slideRect, trackRect);
    const centerDistance = Math.abs((slideRect.left + (slideRect.width / 2)) - trackCenter);

    if (trackIntersection > strongestIntersection || (trackIntersection === strongestIntersection && centerDistance < closestDistance)) {
      matchedSlide = slide;
      strongestIntersection = trackIntersection;
      closestDistance = centerDistance;
    }
  });

  if (matchedSlide && strongestIntersection > 0) {
    return matchedSlide;
  }

  matchedSlide = null;
  closestDistance = Number.POSITIVE_INFINITY;

  slides.forEach((slide) => {
    if (!slide.slide || !slideMatcher(slide.slide)) {
      return;
    }

    const slideRect = slide.slide.getBoundingClientRect();
    const centerDistance = Math.abs((slideRect.left + (slideRect.width / 2)) - trackCenter);

    if (centerDistance < closestDistance) {
      matchedSlide = slide;
      closestDistance = centerDistance;
    }
  });

  return matchedSlide;
}

function applyIncomingFeaturedPersonagem(carousel, names) {
  const incomingCarouselSlide = getBestPersonagensSlideMatch(carousel, (slide) => slide.classList.contains('is-active'));
  const incomingNameSlide = getBestPersonagensSlideMatch(names, (slide) => slide.classList.contains('is-active'));

  if (incomingCarouselSlide && incomingCarouselSlide.slide) {
    incomingCarouselSlide.slide.classList.add(featuredSlideClassName);
  }

  if (incomingNameSlide && incomingNameSlide.slide) {
    incomingNameSlide.slide.classList.add(featuredNameSlideClassName);
  }
}

function updateFeaturedPersonagem(carousel, names) {
  clearFeaturedPersonagem(carousel, featuredSlideClassName);
  clearFeaturedPersonagem(names, featuredNameSlideClassName);
  applyFeaturedPersonagem(carousel, names);
}

function queueIncomingFeaturedPersonagem(carousel, names, currentFrame) {
  if (currentFrame.value) {
    cancelAnimationFrame(currentFrame.value);
  }

  currentFrame.value = requestAnimationFrame(() => {
    currentFrame.value = 0;
    applyIncomingFeaturedPersonagem(carousel, names);
  });
}

function queueFeaturedPersonagemUpdate(carousel, names, currentFrame) {
  if (currentFrame.value) {
    cancelAnimationFrame(currentFrame.value);
  }

  currentFrame.value = requestAnimationFrame(() => {
    currentFrame.value = requestAnimationFrame(() => {
      currentFrame.value = 0;
      updateFeaturedPersonagem(carousel, names);
    });
  });
}

function getPersonagensSlideWidth(section) {
  const slide = section.querySelector('.personagens__carousel .personagens__slide');

  if (!slide) {
    return 0;
  }

  return slide.getBoundingClientRect().width;
}

function isLayoutVisible(layout) {
  if (!(layout instanceof Element)) {
    return true;
  }

  return layout.getClientRects().length > 0 && getComputedStyle(layout).display !== 'none';
}

function shouldMountSection(section, carouselEl) {
  if (!carouselEl || !section.isConnected) {
    return false;
  }

  const layout = section.closest('.layout-desktop, .layout-mobile');

  if (!isLayoutVisible(layout)) {
    return false;
  }

  return true;
}

function mountPersonagens(section) {
  const carouselEl = section.querySelector('.personagens__carousel.splide');
  const namesEl = section.querySelector('.personagens__names.splide');
  const baseOptions = getPersonagensBaseOptions(section);

  if (!carouselEl || !namesEl) {
    return;
  }

  const carousel = new Splide(carouselEl, {
    ...baseOptions,
    autoplay: 'pause',
    pauseOnHover: false,
    pauseOnFocus: false,
    drag: true,
    keyboard: false,
  });

  const names = new Splide(namesEl, {
    ...baseOptions,
    autoplay: false,
    drag: false,
    keyboard: false,
  });

  const startIndex = Number(baseOptions.start) || 0;
  let textureOffset = 0;
  let textureStep = 0;
  let lastDestinationIndex = startIndex;
  let isDesktopActive = false;
  let isMobileActive = false;
  let sectionObserver = null;
  let viewportActivityFrame = 0;
  let spotlightTimeoutId = 0;
  let hasPlayedSectionSpotlight = false;
  const featuredUpdateFrame = {
    value: 0,
  };

  function playFeaturedSpotlight() {
    const featuredSlide = getPersonagensCenteredSlide(carousel);

    if (!featuredSlide || !featuredSlide.slide) {
      return;
    }

    if (spotlightTimeoutId) {
      clearTimeout(spotlightTimeoutId);
    }

    clearFeaturedSpotlight(carousel);
    featuredSlide.slide.classList.add(featuredSpotlightClassName);
    spotlightTimeoutId = window.setTimeout(() => {
      clearFeaturedSpotlight(carousel);
      spotlightTimeoutId = 0;
    }, 420);
  }

  function maybePlaySectionSpotlight() {
    if (hasPlayedSectionSpotlight || !section.classList.contains('section-reveal--entered')) {
      return;
    }

    hasPlayedSectionSpotlight = true;
    requestAnimationFrame(() => {
      playFeaturedSpotlight();
    });
  }

  function syncAutoplayState() {
    const autoplay = carousel.Components.Autoplay;

    if (!autoplay) {
      return;
    }

    const isSectionActive = isPageSliderActive() ? isDesktopActive : isMobileActive;

    if (!shouldAllowAutoplay() || !isSectionActive) {
      autoplay.pause();
      return;
    }

    autoplay.play();
  }

  function mountSectionObserver() {
    if (sectionObserver || isPageSliderActive()) {
      return;
    }

    sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target !== section) {
          return;
        }

        isMobileActive = entry.isIntersecting && entry.intersectionRatio > mobileSectionObserverThreshold;
        syncAutoplayState();
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: [0, mobileSectionObserverThreshold, 0.75, 1],
    });

    sectionObserver.observe(section);
  }

  function unmountSectionObserver() {
    if (!sectionObserver) {
      return;
    }

    sectionObserver.disconnect();
    sectionObserver = null;
    isMobileActive = false;
  }

  function updateViewportActivity() {
    isDesktopActive = getInitialDesktopActiveSection() === section;

    if (isPageSliderActive()) {
      unmountSectionObserver();
      syncAutoplayState();
      return;
    }

    mountSectionObserver();
    isMobileActive = getViewportVisibilityRatio(section) > mobileSectionObserverThreshold;
    syncAutoplayState();
    maybePlaySectionSpotlight();
  }

  function queueViewportActivityUpdate() {
    if (viewportActivityFrame) {
      cancelAnimationFrame(viewportActivityFrame);
    }

    viewportActivityFrame = requestAnimationFrame(() => {
      viewportActivityFrame = 0;
      updateViewportActivity();
    });
  }

  function refreshMountedCarousels() {
    carousel.refresh();
    names.refresh();
    refreshTextureMetrics();
    queueFeaturedPersonagemUpdate(carousel, names, featuredUpdateFrame);
  }

  function handleActiveSectionChange(event) {
    const activeSection = event.detail && event.detail.section;
    const viaKeyboard = Boolean(event.detail && event.detail.viaKeyboard);

    isDesktopActive = activeSection === section;
    syncAutoplayState();

    if (activeSection !== section) {
      return;
    }

    requestAnimationFrame(() => {
      refreshMountedCarousels();
      maybePlaySectionSpotlight();

      if (carouselEl.offsetParent !== null) {
        if (viaKeyboard) {
          carouselEl.focus();
        }
      }
    });
  }

  function handleKeydown(event) {
    const target = event.target;
    const isEditableField = target.closest('input, textarea, select, [contenteditable="true"]');

    if (isEditableField) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      carousel.go('<');
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      carousel.go('>');
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      event.stopPropagation();
      carousel.go(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      event.stopPropagation();
      carousel.go(carousel.Components.Controller.getEnd());
    }
  }

  function applyTextureOffset() {
    section.style.setProperty('--personagens-texture-offset-x', `${textureOffset.toFixed(2)}px`);
  }

  function refreshTextureMetrics() {
    textureStep = getPersonagensSlideWidth(section);
  }

  carousel.sync(names);

  carousel.on('mounted resized updated refresh', () => {
    queueFeaturedPersonagemUpdate(carousel, names, featuredUpdateFrame);

    if (!textureStep) {
      refreshTextureMetrics();
    }
  });

  carousel.on('move', (_, __, destinationIndex) => {
    if (featuredUpdateFrame.value) {
      cancelAnimationFrame(featuredUpdateFrame.value);
      featuredUpdateFrame.value = 0;
    }

    clearFeaturedPersonagem(carousel, featuredSlideClassName);
    clearFeaturedPersonagem(names, featuredNameSlideClassName);

    const normalizedDestination = Number(destinationIndex);

    if (!Number.isFinite(normalizedDestination)) {
      return;
    }

    queueIncomingFeaturedPersonagem(carousel, names, featuredUpdateFrame);

    if (!textureStep) {
      refreshTextureMetrics();
    }

    const delta = normalizedDestination - lastDestinationIndex;
    lastDestinationIndex = normalizedDestination;
    textureOffset -= delta * textureStep;
    applyTextureOffset();
  });

  carousel.on('moved', () => {
    queueFeaturedPersonagemUpdate(carousel, names, featuredUpdateFrame);
  });

  carousel.on('resized updated refresh', () => {
    refreshTextureMetrics();
  });
  carousel.on('destroy', () => {
    if (featuredUpdateFrame.value) {
      cancelAnimationFrame(featuredUpdateFrame.value);
    }

    if (viewportActivityFrame) {
      cancelAnimationFrame(viewportActivityFrame);
    }

    unmountSectionObserver();
    const autoplay = carousel.Components.Autoplay;

    if (autoplay) {
      autoplay.pause();
    }

    if (spotlightTimeoutId) {
      clearTimeout(spotlightTimeoutId);
    }

    carouselEl.onkeydown = null;
    document.removeEventListener(activeSectionChangeEventName, handleActiveSectionChange);
    window.removeEventListener('resize', queueViewportActivityUpdate);
    window.removeEventListener('scroll', queueViewportActivityUpdate);
    section.removeEventListener('section-reveal:entered', maybePlaySectionSpotlight);
  });

  names.mount();
  carousel.mount();
  carouselEl.onkeydown = handleKeydown;
  document.addEventListener(activeSectionChangeEventName, handleActiveSectionChange);
  window.addEventListener('resize', queueViewportActivityUpdate);
  window.addEventListener('scroll', queueViewportActivityUpdate, {
    passive: true,
  });
  section.addEventListener('section-reveal:entered', maybePlaySectionSpotlight);
  applyTextureOffset();
  updateViewportActivity();
  maybePlaySectionSpotlight();

  personagensSplides.set(section, {
    carousel,
    names,
  });
}

export function syncPersonagens() {
  const sections = document.querySelectorAll('.personagens');

  sections.forEach((section) => {
    const carouselEl = section.querySelector('.personagens__carousel.splide');
    const isVisible = shouldMountSection(section, carouselEl);
    const instances = personagensSplides.get(section);

    if (isVisible && !instances) {
      mountPersonagens(section);
      return;
    }

    if (!isVisible && instances) {
      instances.carousel.destroy(true);
      instances.names.destroy(true);
      personagensSplides.delete(section);
      return;
    }

    if (isVisible && instances) {
      instances.carousel.refresh();
      instances.names.refresh();
    }
  });
}
